import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight, MapTrifold, ClipboardText, Scales, Truck, CheckCircle, Lightning, Crown } from "@phosphor-icons/react";
import { useAuth } from "@/auth/AuthContext";

const STEPS = [
  {
    icon: Lightning, color: "#ff8c00",
    title: "Welcome to Load Logic!",
    body: "Your dispatch yard is live. We've seeded 12 demo assets, 5 sample jobs, and 3 axle templates so you can poke around immediately. Replace them with your real data whenever you're ready.",
    cta: "Show me around",
  },
  {
    icon: MapTrifold, color: "#4682b4",
    title: "Live Map",
    body: "Watch every truck, dumpster, and excavator move in real time. Click any asset for details, draw geofences, and get entry/exit alerts.",
    cta: "Got it — what's next?",
  },
  {
    icon: ClipboardText, color: "#ff8c00",
    title: "Dispatch Board",
    body: "Drag jobs through the pipeline: Scheduled → Dispatched → In Progress → Completed. Assign trucks with a click and generate PDF run-sheets.",
    cta: "Got it",
  },
  {
    icon: Scales, color: "#22c55e",
    title: "Federal Bridge Formula",
    body: "Every job over 0 lbs is auto-checked against 49 CFR § 658.17. You'll see a banner on overweight jobs. Flip Settings → Dispatch Gate to BLOCK if you want a hard stop.",
    cta: "Got it",
  },
  {
    icon: Truck, color: "#ff8c00",
    title: "Driver Mobile",
    body: "Drivers get their own /driver view — status advance, photo proof of delivery, offline queue for dead zones. Install as a PWA on iOS/Android.",
    cta: "Got it",
  },
  {
    icon: CheckCircle, color: "#22c55e",
    title: "You're ready.",
    body: "Your 14-day trial is running. When you're set, hit Settings → Billing to choose a plan. Anything weird, just reply to your welcome email.",
    cta: "Let's dispatch!",
  },
];

const STORAGE_KEY = "ll_tour_seen";

export default function DemoTour() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Only on dashboard routes
    if (!location.pathname.startsWith("/live")
      && !location.pathname.startsWith("/assets")
      && !location.pathname.startsWith("/jobs")
      && !location.pathname.startsWith("/compliance")
      && !location.pathname.startsWith("/history")) return;
    try {
      const isFirstRun = localStorage.getItem("ll_first_run") === "1";
      const alreadySeen = localStorage.getItem(STORAGE_KEY) === "1";
      if (isFirstRun && !alreadySeen) {
        setOpen(true);
        localStorage.removeItem("ll_first_run");
      }
    } catch (_e) {}
  }, [user, location.pathname]);

  if (!open) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_e) {}
    setOpen(false);
  };

  const next = () => {
    if (isLast) { close(); return; }
    if (step === 1) navigate("/live");
    if (step === 2) navigate("/jobs");
    if (step === 3) navigate("/compliance");
    setStep(step + 1);
  };

  return (
    <div className="fixed inset-0 z-[4000] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="demo-tour">
      <div className="bg-[#0d0d0d] border w-full max-w-md" style={{ borderColor: current.color }}>
        <div className="p-5 border-b border-[#2a2a2a] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <current.icon size={22} weight="fill" style={{ color: current.color }} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: current.color }}>
                Step {step + 1} of {STEPS.length}
              </div>
              <div className="font-heading font-black tracking-tight">{current.title}</div>
            </div>
          </div>
          <button
            onClick={close}
            className="w-8 h-8 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] flex items-center justify-center"
            data-testid="tour-close"
          >
            <X size={12} />
          </button>
        </div>
        <div className="p-5">
          <p className="text-sm text-[#a1a1aa] leading-relaxed mb-5">{current.body}</p>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1 mb-5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1 transition-all"
                style={{
                  width: i === step ? "20px" : "8px",
                  background: i <= step ? current.color : "#2a2a2a",
                }}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              onClick={close}
              className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] hover:text-white font-bold"
              data-testid="tour-skip"
            >
              Skip tour
            </button>
            <button
              onClick={next}
              className="px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] font-black flex items-center gap-2"
              style={{ background: current.color, color: current.color === "#22c55e" || current.color === "#ff8c00" ? "#000" : "#fff" }}
              data-testid="tour-next"
            >
              {current.cta} <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
